import type { IndustryContent } from '@/types/content';

export const treeServiceContent: IndustryContent = {
  slug: 'tree-service',
  name: 'Tree Service',
  headline: 'The Operating System for Tree Service Companies',
  subheadline:
    'Manage crews, track equipment, and handle complex multi-day jobs—all from one platform built for arborists and tree care pros.',
  heroImageUrl: '/images/industries/tree-service-hero.webp',
  painPoints: [
    {
      title: 'Estimates are time-consuming',
      description:
        'Driving to every property for a quote eats up your day. And customers ghost you before you can follow up.',
      icon: 'estimate',
    },
    {
      title: 'Equipment tracking is a mess',
      description:
        'Chainsaws, chippers, stump grinders—knowing which truck has what equipment and when maintenance is due is a constant struggle.',
      icon: 'equipment',
    },
    {
      title: 'Multi-day jobs are hard to manage',
      description:
        'Large removals span multiple days with different crew configurations. Keeping everyone coordinated is challenging.',
      icon: 'calendar',
    },
    {
      title: 'Liability documentation gaps',
      description:
        'Before photos, signed waivers, property line confirmations—one missed document can cost you in a dispute.',
      icon: 'document',
    },
    {
      title: 'Storm work overwhelms you',
      description:
        'After a storm, the phone rings off the hook. Prioritizing emergencies while tracking regular work becomes chaos.',
      icon: 'storm',
    },
  ],
  features: [
    {
      title: 'Visual Estimating',
      description:
        'Send estimates with satellite imagery, markup tools, and digital signatures. Close deals faster without extra site visits.',
      icon: 'estimate',
    },
    {
      title: 'Equipment Management',
      description:
        'Track every piece of equipment by truck. Set maintenance schedules and get alerts before breakdowns happen.',
      icon: 'equipment',
    },
    {
      title: 'Multi-Day Job Scheduling',
      description:
        'Schedule complex jobs across multiple days with different crew assignments. Track progress and adjust on the fly.',
      icon: 'calendar',
    },
    {
      title: 'Documentation & Compliance',
      description:
        'Digital waivers, before/after photos, and property documentation attached to every job. Protect yourself from disputes.',
      icon: 'document',
    },
    {
      title: 'Emergency Dispatch',
      description:
        'Storm mode prioritizes emergency calls, tracks crew availability, and helps you capture surge demand.',
      icon: 'dispatch',
    },
    {
      title: 'Crew Safety Tracking',
      description:
        'Track certifications, safety training, and equipment inspections. Stay compliant and keep your team safe.',
      icon: 'safety',
    },
  ],
  testimonial: {
    id: 'tree-service-1',
    quote:
      'After Hurricane season, we handled 3x our normal volume without losing track of a single job. The emergency dispatch feature was a lifesaver.',
    author: 'Carlos Martinez',
    role: 'Operations Manager',
    company: 'Martinez Tree Experts',
    industry: 'tree-service',
    stats: {
      label: 'Volume Handled',
      value: '3x',
    },
  },
  stats: [
    { label: 'Faster Estimates', value: '50%' },
    { label: 'Equipment Downtime', value: '-40%' },
    { label: 'Jobs Per Crew', value: '+20%' },
    { label: 'Customer Satisfaction', value: '96%' },
  ],
  seo: {
    title: 'Tree Service Software | Scheduling & Equipment Tracking | Omniforge',
    description:
      'Tree service software for arborists and tree care companies. Estimating, equipment tracking, multi-day scheduling, and storm dispatch. Start your free trial.',
    keywords: [
      'tree service software',
      'arborist software',
      'tree care software',
      'tree removal software',
      'tree service scheduling',
    ],
  },
};
